import React, { useState } from 'react';
import MultiSelectCandidateDropdown from '../../../common/multiSelectCandidate';
import { resolveToOptionObj, resolveToOptionObjArray } from './utils';

// Fully controlled component - no local state for text inputs.
const InfoItem = ({
  label,
  value,
  isEditing,
  onChange,
  name,
  isRequired,
  type = 'text',
  options = [],
  isDisabled,
  disabled = false,
  max,
}) => {
  const [dobError, setDobError] = useState('');

  const handleInputChange = (e) => {
    let val = e.target.value;

    // Allow only digits for specific fields
    const fourDigitFields = ['duration_text', 'duration_total'];
    if (fourDigitFields.includes(name)) {
      val = val.replace(/\D/g, '');
      if (val.length > 4) val = val.slice(0, 4);
    }

    if (name === 'totalduration') {
      val = val.replace(/\D/g, '');
    }

    if (name === 'phone') {
      let cleaned = val.replace(/[^\d+]/g, '');
      if (cleaned.includes('+')) {
        const plusCount = (cleaned.match(/\+/g) || []).length;
        if (plusCount > 1) {
          const digitsOnly = cleaned.replace(/\+/g, '');
          cleaned = val.startsWith('+') ? '+' + digitsOnly : digitsOnly;
        } else if (!cleaned.startsWith('+')) {
          cleaned = '+' + cleaned.replace(/\+/g, '');
        }
      }
      val = cleaned;
    }

    if (name === 'field') {
      val = val.replace(/[^A-Za-z .']/g, '');
    }

    // ── Date of Birth validation ──────────────────────────────────
    if (name === 'date_of_birth' && type === 'date') {
      setDobError('');

      if (!val) {
        // Field cleared — propagate null so parent knows it's empty
        onChange?.(null);
        return;
      }

      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(val)) {
        setDobError('Invalid date format. Use YYYY-MM-DD.');
        onChange?.(null); // ← pass null so Save stays disabled
        return;
      }

      const [year, month, day] = val.split('-').map(Number);

      if (month < 1 || month > 12) {
        setDobError('Month must be between 01 and 12.');
        onChange?.(null);
        return;
      }

      const daysInMonth = new Date(year, month, 0).getDate();
      if (day < 1 || day > daysInMonth) {
        setDobError(`Day must be between 01 and ${daysInMonth} for the selected month.`);
        onChange?.(null);
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const inputDate = new Date(year, month - 1, day);

      if (inputDate > today) {
        setDobError('Date of birth cannot be a future date.');
        onChange?.(null); // ← critical: don't let invalid date reach modalTempData
        return;
      }

      if (year < 1900 || year > today.getFullYear()) {
        setDobError(`Year must be between 1900 and ${today.getFullYear()}.`);
        onChange?.(null);
        return;
      }

      // All checks passed — propagate valid date
      onChange?.(val);
      return;
    }
    // ─────────────────────────────────────────────────────────────

    const maxLength = name === 'phone' ? 15 : 100;
    if (val.length > maxLength) val = val.slice(0, maxLength);

    onChange?.(val);
  };

  // Gender dropdown
  if (isEditing && type === 'select' && name === 'gender') {
    let selectedOption = null;
    if (value) {
      if (typeof value === 'object' && (value.value || value.id)) {
        selectedOption = value;
      } else {
        const valueStr = typeof value === 'string' ? value.toLowerCase() : String(value).toLowerCase();
        selectedOption = options.find(opt =>
          opt.value?.toLowerCase() === valueStr ||
          opt.id?.toLowerCase() === valueStr ||
          opt.label?.toLowerCase() === valueStr
        );
      }
    }

    return (
      <div className="w-full">
        <p className="text-xs text-gray-999">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </p>
        <MultiSelectCandidateDropdown
          label=""
          name={name}
          options={options}
          selected={selectedOption}
          onChange={(selected) => {
            const val =
              selected?.target?.value?.value ||
              selected?.target?.value?.id ||
              selected?.target?.value?.label?.toLowerCase() ||
              selected?.value?.value ||
              selected?.value?.id ||
              selected?.value ||
              selected?.label?.toLowerCase() ||
              '';
            onChange?.(val);
          }}
          isMultiSelect={false}
        />
      </div>
    );
  }

  // Location / nationality / dual_nationality dropdown
  if (
    isEditing &&
    type === 'select' &&
    (name === 'location' || name === 'nationality' || name === 'dual_nationality') &&
    Array.isArray(options)
  ) {
    let selectedOption;
    if (name === 'dual_nationality') {
      selectedOption = resolveToOptionObjArray(value, options);
    } else {
      if (value === 'do not disclose') selectedOption = null;
      else selectedOption = resolveToOptionObj(value, options);
    }

    return (
      <div className="w-full">
        <p className="text-xs text-gray-999">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </p>
        <MultiSelectCandidateDropdown
          label=""
          name={name}
          options={options}
          selected={selectedOption}
          onChange={(selected) => {
            if (!selected?.target?.value) {
              onChange?.(null);
              return;
            }
            let val;
            if (name === 'dual_nationality') {
              if (Array.isArray(selected.target.value)) {
                val = selected.target.value.map(opt => ({
                  id: opt?.id,
                  label: opt?.label,
                  value: opt?.value,
                }));
              } else if (selected.target.value) {
                val = [{
                  id: selected.target.value?.id,
                  label: selected.target.value?.label,
                  value: selected.target.value?.value,
                }];
              } else {
                val = [];
              }
            } else {
              val = selected.target.value;
            }
            onChange?.(val);
          }}
          disabled={isDisabled}
          isMultiSelect={name === 'dual_nationality'}
        />
      </div>
    );
  }

  return (
    <div className="w-full">

      {isEditing && type === 'checkbox' ? (
        <label className="flex items-center gap-2 mt-1 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange?.(e.target.checked)}
            className="cursor-pointer"
          />
          <span className="text-xs text-gray-999">{label}</span>
        </label>
      ) : (
        <>
          <p className="text-xs text-gray-999 mb-1">
            {label} {isRequired && <span className="text-red-500">*</span>}
          </p>

          {isEditing ? (
            <>
              <input
                type={type}
                name={name}
                value={value ?? ''}
                onChange={handleInputChange}
                disabled={disabled || isDisabled}
                autoComplete="off"
                max={name === 'date_of_birth' ? new Date().toISOString().split('T')[0] : max}
                className={`font-semibold text-gray-333 text-xs mt-1 p-1 border rounded-md w-3/4 ${
                  dobError && name === 'date_of_birth'
                    ? 'border-red-400 focus:ring-red-400'
                    : 'border-gray-300'
                }`}
              />
              {dobError && name === 'date_of_birth' && (
                <p className="text-xs text-red-500 mt-1">{dobError}</p>
              )}
            </>
          ) : (
            <p className="font-semibold text-gray-333 text-xs mt-1 break-words whitespace-pre-line">
              {value || 'Nil'}
            </p>
          )}
        </>
      )}

    </div>
  );
};

export default InfoItem;
